/**
 * The visitor's own ad tags: the UTMs and click ids a paid click lands with. A landing page that
 * links to the platform sign-up must hand these over untouched, or the platform credits the signup
 * to the page instead of the ad and Meta, Google and TikTok cannot match the conversion.
 *
 * `ajs_aid` is deliberately absent: carrying an analytics identity across hosts is a separate
 * decision from carrying campaign tags.
 */
export const INBOUND_UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export const INBOUND_CLICK_ID_PARAMS = [
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'ttp',
  'msclkid',
  'rdt_cid',
  'li_fat_id',
  'sccid',
] as const;

const INBOUND_PARAMS: readonly string[] = [...INBOUND_UTM_PARAMS, ...INBOUND_CLICK_ID_PARAMS, 'partner'];

/**
 * The page's own context on a portfolio sign-up link: which portfolio, which surface, which button.
 * No ad sets these, so they survive a paid click whose UTMs replace the page's.
 */
export const PORTFOLIO_SIGN_UP_CONTEXT_PARAMS = [
  'portfolio_code',
  'portfolio_url',
  'portfolio_entry',
  'cta_location',
] as const;

/**
 * Every key a portfolio surface puts on a link to the platform sign-up. The platform's search-param
 * allowlist must accept all of them, or `metadata.search_params` silently loses them
 * (contract test: `apps/platform/utils/get-allowlisted-search-params.spec.ts`).
 */
export const PORTFOLIO_SIGN_UP_EMITTED_PARAMS: readonly string[] = [
  ...INBOUND_PARAMS,
  ...PORTFOLIO_SIGN_UP_CONTEXT_PARAMS,
  'redirect_url',
];

/** Allowlisted inbound key → value. Only keys that arrived non-empty are present. */
export type InboundAttribution = Readonly<Record<string, string>>;

export const NO_INBOUND_ATTRIBUTION: InboundAttribution = Object.freeze({});

export function readInboundAttribution(search: string | Pick<URLSearchParams, 'get'>): InboundAttribution {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const inbound: Record<string, string> = {};
  for (const key of INBOUND_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) {
      inbound[key] = value;
    }
  }
  return inbound;
}

export function hasInboundUtm(inbound: InboundAttribution): boolean {
  return INBOUND_UTM_PARAMS.some((key) => Boolean(inbound[key]));
}

/**
 * Folds the visitor's tags into a sign-up href built with the page's own defaults.
 *
 * The UTM set is replaced whole, never merged: an ad that sets only `utm_source` must not reach
 * sign-up as `utm_source=tiktok&utm_medium=landing`, a pair neither the ad nor the page sent. So when
 * any inbound UTM is present every page UTM is dropped first. The page's own context (which arm,
 * which button) travels in non-UTM keys (`portfolio_entry`, `cta_location`) that this never touches.
 * Click ids and `partner` are always forwarded when present.
 */
export function applyInboundAttribution(href: string, inbound: InboundAttribution): string {
  const url = new URL(href);

  if (hasInboundUtm(inbound)) {
    for (const key of INBOUND_UTM_PARAMS) {
      url.searchParams.delete(key);
    }
  }

  for (const key of INBOUND_PARAMS) {
    const value = inbound[key];
    if (value) {
      url.searchParams.set(key, value);
    }
  }

  return url.toString();
}
